import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import {
    ArrayOfPortailDocumentCategorieDto,
    ArrayOfPortailStatutDto,
    FichierCompletModel,
    MutationGetListePortailDocumentCategorieGeneriqueArgs,
    MutationGetListePortailStatutGeneriqueArgs,
    PortailDocumentCategorieDto,
    PortailStatutDto
} from 'src/graphql/generated-types';
import { FormValidator } from 'src/app/validators/form-validator';
import { filter, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import gql from 'graphql-tag';
import { forkJoin, Observable, of } from 'rxjs';
import { buildProviders, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { environment } from 'src/environments/environment';
import {
    DocumentCategoryEditDialogComponent,
    DocumentCategoryEditDialogData
} from 'src/app/_shared/dialogs/document-category-edit-dialog/document-category-edit-dialog.component';

const LABEL_MAX_LENGTH = 255;
const DESCRIPTION_MAX_LENGTH = 255;

export type DocumentBankLocalFile = FichierCompletModel & { IdStatut: number, IdCategorie: number };

@Component({
    selector: 'app-document-bank-create-document',
    templateUrl: './document-bank-local-file.component.html',
    styleUrls: ['./document-bank-local-file.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(DocumentBankLocalFileComponent),
    standalone: false
})
export class DocumentBankLocalFileComponent extends CustomFormControlAccess implements OnInit {

    allowedExtensions = environment.extensions.all;

    form: UntypedFormGroup;

    shouldDisplayDescription = false;
    shouldDisplayPostDateRange = false;

    fileName: string = null;

    statusList: PortailStatutDto[] = [];
    categoryList: PortailDocumentCategorieDto[] = [];

    selectedDocument: FichierCompletModel = null; // utilisé uniquement en cas de modification

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly dialogService: KalDialogService,
                private readonly fb: UntypedFormBuilder) {
        super();
    }

    writeValue(value: DocumentBankLocalFile): void {
        this.selectedDocument = value;

        this.fileName = this.selectedDocument?.Filename || null;

        this.form.patchValue({
            ...value,
            DatePublicationDocument: {
                startDate: value?.DateDebut || null,
                endDate: value?.DateFin || null
            }
        }, {emitEvent: false});

        if (!!value?.RemarqueLien) {
            this.toggleDescription();
        }

        if (!!value?.DateDebut || !!value?.DateFin) {
            this.togglePostDateRange();
        }

        if (this.fileName) {
            this.form.get('Fichier').clearValidators();
            this.form.get('Fichier').updateValueAndValidity();
        }

        super.writeValue(value);
    }

    validate(c: AbstractControl): Observable<ValidationErrors> {
        return of(this.form.valid ? null : {documentlocalfile: true});
    }

    toggleDescription(): void {
        this.shouldDisplayDescription = !this.shouldDisplayDescription;
    }

    togglePostDateRange(): void {
        this.shouldDisplayPostDateRange = !this.shouldDisplayPostDateRange;
    }

    openCategoryEditDialog(): void {
        const dialogConfig = new KalDialogConfig<DocumentCategoryEditDialogData>({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop'
        });

        this.watch$ = this.dialogService.open(DocumentCategoryEditDialogComponent, dialogConfig).afterClosed.pipe(
            filter(dialogResult => !!dialogResult?.accepted),
            tap(({categoryList}) => {
                this.form.get('IdCategorie').patchValue(null, {emitEvent: false})
                this.watch$ = this.getCategoryList$();
                this.categoryList = categoryList;
                this.cdr.markForCheck();
            })
        );
    }

    private getStatusList$(): Observable<ArrayOfPortailStatutDto> {
        return this.graphqlService.mutateOne<'GetListePortailStatutGenerique', MutationGetListePortailStatutGeneriqueArgs>({
            mutation: gql`
                mutation GetListePortailStatutGenerique(
                    $contexte: ContextInfosInput
                    $criteres: CriteresPortailStatutDTOInput
                ) {
                    GetListePortailStatutGenerique(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        PortailStatutDTO {
                            Code
                            Id
                            IsActif
                            Libelle
                        }
                    }
                }
            `
        }).pipe(
            tap(GetListePortailStatutGenerique => this.statusList = GetListePortailStatutGenerique?.PortailStatutDTO || [])
        );
    }

    private getCategoryList$(): Observable<ArrayOfPortailDocumentCategorieDto> {
        return this.graphqlService.mutateOne<'GetListePortailDocumentCategorieGenerique', MutationGetListePortailDocumentCategorieGeneriqueArgs>({
            mutation: gql`
                mutation GetListePortailDocumentCategorieGenerique(
                    $contexte: ContextInfosInput
                    $criteres: CriteresPortailDocumentCategorieDTOInput
                ) {
                    GetListePortailDocumentCategorieGenerique(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        PortailDocumentCategorieDTO {
                            Code
                            Id
                            Libelle
                        }
                    }
                }
            `
        }).pipe(
            tap(GetListePortailDocumentCategorieGenerique => this.categoryList = GetListePortailDocumentCategorieGenerique?.PortailDocumentCategorieDTO || [])
        );
    }

    private normalize(): DocumentBankLocalFile {
        const {
            Fichier,
            LibelleLien,
            IdStatut,
            IdCategorie,
            RemarqueLien,
            DatePublicationDocument,
            IsCommentable,
            IsLikeable
        } = this.form.value;

        return {
            // pour ces 5 premiers critères statiques:
            // si on est en mode modification d'un document -> on touche à rien
            // sinon, c'est qu'on est en mode création, donc on nullifie les valeurs
            IdLienFichier: this.selectedDocument?.IdLienFichier ?? 0,
            IdFile: this.selectedDocument?.IdFile ?? null,
            Taille: this.selectedDocument?.Taille ?? 0,
            TypeFichierIcone: this.selectedDocument?.TypeFichierIcone ?? null,
            TypeFichierLibelle: this.selectedDocument?.TypeFichierLibelle ?? null,
            Url: this.selectedDocument?.Url ?? null,
            LibelleLien,
            Filename: this.fileName || this.selectedDocument?.Filename,
            FileBytes: Fichier?.fileBlob?.split(',')?.[1] || this.selectedDocument?.FileBytes,
            StatutCode: this.statusList.find(s => s.Id === IdStatut)?.Code || null,
            EditCategCode: this.categoryList.find(c => c.Id === IdCategorie)?.Code || null,
            IdStatut,
            IdCategorie,
            Extension: Fichier?.extension || this.selectedDocument?.Extension,
            RemarqueLien,
            DateDebut: DatePublicationDocument?.startDate || null,
            DateFin: DatePublicationDocument?.endDate || null,
            IsCommentable,
            IsLikeable
        };
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            Fichier: [null, [FormValidator.isNotNull]],
            LibelleLien: [null, [FormValidator.isNotNull, Validators.maxLength(LABEL_MAX_LENGTH)]],
            IdStatut: [null, [FormValidator.isNotNull]],
            IdCategorie: [null],
            RemarqueLien: [null, [Validators.maxLength(DESCRIPTION_MAX_LENGTH)]],
            DatePublicationDocument: [{
                startDate: null,
                endDate: null
            }],
            IsCommentable: [false],
            IsLikeable: [false]
        });

        this.watch$ = this.form.get('Fichier').valueChanges.pipe(
            tap(value => {
                this.fileName = value?.file?.name;
                this.cdr.markForCheck();
            })
        );

        this.watch$ = forkJoin([
            this.getStatusList$(),
            this.getCategoryList$()
        ]).pipe(
            tap(() => {
                this.fileName = this.selectedDocument?.Filename || null;
                this.cdr.markForCheck();
            })
        );

        this.watch$ = this.form.valueChanges.pipe(
            tap(() => {
                this.notifyUpdate(this.normalize());
                this.cdr.markForCheck();
            })
        );
    }

}
